import {FirebaseApp, getApps, initializeApp} from 'firebase/app';
import {
  Auth,
  EmailAuthProvider,
  GoogleAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type UserCredential,
  updatePassword as firebaseUpdatePassword,
} from 'firebase/auth';
import axios from 'axios';
import {BASE_URL} from './Urls';
import {config} from '../config';

let auth: Auth | undefined;

// Plain client for the login profile call: the app-wide interceptors would
// treat its 401s ("verify your email") as an expired session and reload.
const loginClient = axios.create();

/** Lazily initialised so the legacy-JWT setup never needs Firebase config. */
export const getFirebaseAuth = (): Auth => {
  if (!auth) {
    const app: FirebaseApp =
      getApps()[0] ??
      initializeApp({
        apiKey: config.firebaseApiKey,
        authDomain: config.firebaseAuthDomain,
        projectId: config.firebaseProjectId,
        appId: config.firebaseAppId,
      });
    auth = getAuth(app);
  }
  return auth;
};

// The SDK restores the signed-in user from IndexedDB asynchronously after a
// page load; wait for that before deciding there is no user.
const currentUser = async () => {
  const fbAuth = getFirebaseAuth();
  await fbAuth.authStateReady();
  return fbAuth.currentUser;
};

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'Invalid email or password',
  'auth/invalid-email': 'Invalid email or password',
  'auth/user-not-found': 'Invalid email or password',
  'auth/wrong-password': 'Invalid email or password',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/weak-password': 'Password must be at least 6 characters',
  'auth/requires-recent-login': 'Please sign in again before changing your password',
  'auth/popup-closed-by-user': 'Google sign-in was cancelled',
  'auth/popup-blocked': 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.',
  'auth/account-exists-with-different-credential':
    'This email already uses a different sign-in method. Sign in with email and password.',
};

const failure = (error: any, fallback: string) => ({
  isNetwork: error?.code === 'ERR_NETWORK' || error?.code === 'auth/network-request-failed',
  status: error?.response?.status || (error?.code?.startsWith('auth/') ? 401 : 500),
  success: false,
  data: null,
  message:
    error?.response?.data?.message || FIREBASE_ERROR_MESSAGES[error?.code] || error?.message || fallback,
});

const UNVERIFIED_EMAIL = /verify your email/i;

export class FirebaseAuthApiModule {
  /**
   * Signs in with Firebase, then loads the diibs profile from the backend,
   * which links the Firebase account to the existing user on first sign-in.
   */
  async login(email: string, password: string) {
    const fbAuth = getFirebaseAuth();
    return this.completeSignIn(() => signInWithEmailAndPassword(fbAuth, email, password), email);
  }

  /**
   * Google sign-in in a popup. Google confirms the email, so the backend links
   * staff accounts on the first sign-in without a verification email.
   */
  async signInWithGoogle() {
    const fbAuth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({prompt: 'select_account'});
    return this.completeSignIn(() => signInWithPopup(fbAuth, provider));
  }

  private async completeSignIn(signIn: () => Promise<UserCredential>, email?: string) {
    const fbAuth = getFirebaseAuth();
    try {
      const {user} = await signIn();
      const idToken = await user.getIdToken();

      const response = await loginClient.get(`${BASE_URL}/common/getProfile`, {
        headers: {Authorization: `Bearer ${idToken}`},
      });
      const profile = response.data?.data;

      return {
        status: 200,
        success: true,
        data: {
          ...profile,
          // Routing reads `role` (legacy) or `roleName`; provide both.
          role: profile?.roleName,
          isStripeConnect: profile?.isStripeConnect ? 1 : 0,
          email: user.email ?? email ?? '',
          emailVerified: user.emailVerified,
          api_token: idToken,
          token: idToken,
        },
        message: 'Login successful',
      };
    } catch (error: any) {
      const message: string | undefined = error?.response?.data?.message;
      if (fbAuth.currentUser && message && UNVERIFIED_EMAIL.test(message)) {
        await sendEmailVerification(fbAuth.currentUser).catch(() => undefined);
        await signOut(fbAuth);
        return {
          status: 403,
          success: false,
          data: null,
          message: "We've emailed you a verification link. Please verify your email, then sign in again.",
        };
      }
      if (fbAuth.currentUser) await signOut(fbAuth);
      return failure(error, 'Login failed');
    }
  }

  /** Fresh ID token for API calls (the SDK refreshes it before expiry). */
  async getIdToken(): Promise<string | undefined> {
    const user = await currentUser();
    return user ? user.getIdToken() : undefined;
  }

  async forgotPassword(email: string) {
    try {
      await sendPasswordResetEmail(getFirebaseAuth(), email);
      return {status: 200, success: true, data: null, message: 'Password reset email sent'};
    } catch (error: any) {
      // Don't reveal whether the address has an account.
      if (error?.code === 'auth/user-not-found') {
        return {status: 200, success: true, data: null, message: 'Password reset email sent'};
      }
      return failure(error, 'Could not send password reset email');
    }
  }

  async updatePassword({oldPassword, newPassword}: {oldPassword: string; newPassword: string}) {
    try {
      const user = await currentUser();
      if (!user?.email) throw Object.assign(new Error('Not signed in'), {code: 'auth/requires-recent-login'});
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, oldPassword));
      await firebaseUpdatePassword(user, newPassword);
      return {status: 200, success: true, data: null, message: 'Password updated'};
    } catch (error: any) {
      return failure(error, 'Could not update password');
    }
  }

  // Firebase resets passwords and verifies email through emailed links, not
  // codes typed into the app.
  async resetPassword(_userId: string, _code: string, _password: string) {
    return {status: 400, success: false, data: null, message: 'Use the link in your password reset email.'};
  }

  async verifyOtp(_email: string, _otp: string) {
    return {status: 400, success: false, data: null, message: 'Use the link in your verification email.'};
  }

  async resendOtp(_email: string) {
    try {
      const user = await currentUser();
      if (!user) return {status: 400, success: false, data: null, message: 'Sign in to resend the verification email.'};
      await sendEmailVerification(user);
      return {status: 200, success: true, data: null, message: 'Verification email sent'};
    } catch (error: any) {
      return failure(error, 'Could not send verification email');
    }
  }

  async logout() {
    await signOut(getFirebaseAuth());
    return {status: 200, success: true, message: 'Logged out successfully'};
  }
}
